import {
  ApplicationRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { AuthService, LoadingIndicatorService } from "#services/shared";
import { Router } from "@angular/router";
import { UserService } from "#services/api";
import { MsalService, MsalBroadcastService } from "@azure/msal-angular";
import {
  EventMessage,
  EventType,
  InteractionStatus,
} from "@azure/msal-browser";
import { Subject } from "rxjs";
import { filter, finalize, takeUntil } from "rxjs/operators";
import { OKTA_AUTH } from "@okta/okta-angular";
import OktaAuth from "@okta/okta-auth-js";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  loginDisplay = false;
  // Captured once at construction from the router navigation state (in-memory,
  // set by the route guards). It is NOT user-controllable, so it is safe to use
  // as the condition that decides whether to auto-start sign-in.
  private readonly _cameFromProtectedRoute: boolean;
  // MSAL/Okta callbacks can invoke the user-data handlers more than once per
  // login. Guard the post-login navigation so only the first completion routes
  // the user; otherwise a later call finds redirectUrl already consumed and
  // wrongly falls back to the home route, replacing the intended target page.
  private _postLoginHandled = false;
  private readonly _destroying$ = new Subject<boolean>();
  constructor(
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private _authService: AuthService,
    private userSvc: UserService,
    private router: Router,
    private _msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private readonly _appRef: ApplicationRef,
    private readonly _loadingIndicatorService: LoadingIndicatorService
  ) {
    this._cameFromProtectedRoute =
      this.router.getCurrentNavigation()?.extras?.state?.["fromGuard"] === true;
  }

  async ngOnInit() {
    try {
      const isAuthenticatedOkta = await this.oktaAuth.isAuthenticated();
      const isAuthenticatedMSAL = this._authService.isAuthenticated();
      if (isAuthenticatedOkta) {
        await this._getExternalUserData();
      } else if (isAuthenticatedMSAL) {
        this._getUserData();
      } else if (localStorage.getItem("loginOnce") == "true") {
        localStorage.removeItem("loginOnce");
        this.azureLogin();
      } else if (this._cameFromProtectedRoute) {
        // Reached login via a protected deep link (e.g. TA Pro source=tapro).
        // The decision is driven by in-memory router state set by the guard,
        // never by user-controllable storage. Start Chevron sign-in so the user
        // lands on the target page.
        this.azureLogin();
      }
    } catch (error) {
      console.log("Error during authentication check", error);
      console.log("Error details:", error.message);
    }
  }

  /**
   * Returns the stored post-login redirect target only when it is a safe,
   * in-app relative path. The value lives in localStorage, which is
   * attacker-controllable, so it must never be trusted directly to guard a
   * sensitive action or drive navigation (open-redirect / auth-flow bypass).
   * Rejects absolute URLs, protocol-relative URLs (//host) and any scheme.
   */
  private getSafeRedirectUrl(): string | null {
    const url = localStorage.getItem("redirectUrl");
    if (
      !url ||
      !url.startsWith("/") ||
      url.startsWith("//") ||
      url.startsWith("/\\") ||
      url.includes("://")
    ) {
      return null;
    }
    return url;
  }

  azureLogin() {
    this.setLoginDisplay();
    localStorage.setItem("loginOnce", "true");
    this._msalService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        if (this._msalService.instance.getAllAccounts().length === 0) {
          window.location.pathname = "/";
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
        if (this._authService.isAuthenticated()) {
          this._getUserData();
        }
      });
    if (this._authService.isAuthenticated()) {
      this._getUserData();
    } else {
      // No active account yet: start the interactive Chevron sign-in redirect.
      // The button must initiate the redirect directly; wiring up the
      // subscriptions above alone does not begin a new sign-in interaction.
      this._authService.login();
    }
  }

  async oktaLogin() {
    try {
      await this.oktaAuth.signInWithRedirect();
    } catch (error) {
      console.error("Error during Okta login", error);
    }
  }
 visible: boolean = false;
  async _getExternalUserData(): Promise<void> {
    this._loadingIndicatorService.show();
    const oktaToken = this.oktaAuth.getAccessToken();
    sessionStorage.setItem("Token", oktaToken);
    sessionStorage.setItem("UserType","nonchevron"); // Set UserType to nonchevron
    this._authService
      .getExternalUserData(oktaToken)
      .pipe(
        finalize(() => {
          this._appRef.tick();
          this._loadingIndicatorService.hide();
        })
      )
      .subscribe(async() => {
        if (this._postLoginHandled) {
          return;
        }
        this._postLoginHandled = true;
        const isAuthenticated = this.userSvc.isAuthorized();
       // if (this.userSvc.isAuthorized()) {
          const redirectUrl = this.getSafeRedirectUrl();
          localStorage.removeItem("redirectUrl");

          if (redirectUrl) {
            await this.router.navigateByUrl(redirectUrl);
          } else {
           await this.router.navigate([""]);
          }

          if(sessionStorage.getItem("Scheduler") === "false" || sessionStorage.getItem("SystemAdmin") === 'false') {
            this.visible = true;
          }
       // }
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next(true);
    this._destroying$.complete();
  }

  setLoginDisplay() {
    this.loginDisplay = this._msalService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this._msalService.instance.getActiveAccount();
    if (
      !activeAccount &&
      this._msalService.instance.getAllAccounts().length > 0
    ) {
      const accounts = this._msalService.instance.getAllAccounts();
      this._msalService.instance.setActiveAccount(accounts[0]);
    }
    // handle auth redired/do all initial setup for msal
    this._msalService.instance
      .handleRedirectPromise()
      .then(() => {
        // Sign-in is initiated explicitly by azureLogin()/ngOnInit; nothing to
        // do here beyond letting MSAL finish processing the redirect response.
      })
      .catch((err) => {
        console.log(err);
      });
  }

  private _getUserData(): void {
    this._loadingIndicatorService.show();
    sessionStorage.setItem("UserType","chevron"); // Set UserType to chevron
    this._authService
      .getUserData()
      .pipe(
        finalize(() => {
          this._appRef.tick();
          this._loadingIndicatorService.hide();
        })
      )
      .subscribe(async () => {
        if (this.userSvc.isAuthorized()) {
          if (this._postLoginHandled) {
            return;
          }
          this._postLoginHandled = true;
          const redirectUrl = this.getSafeRedirectUrl();
          localStorage.removeItem("redirectUrl");

          if (redirectUrl) {
           await this.router.navigateByUrl(redirectUrl);
          } else {
           await this.router.navigate([""]);
          }
        }
      });
  }
}
